import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Rocket, ArrowRight, Network, Sparkles, Layers, Zap, ShieldCheck } from 'lucide-react';
import { auth, db } from '../firebase';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  icon: any;
  view: string;
}

const CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    id: 'domain',
    title: 'Configurar Domínio',
    description: 'Conecte seu domínio próprio para profissionalizar sua estrutura.',
    icon: Network,
    view: 'domain_settings'
  },
  {
    id: 'copy',
    title: 'Gerar Primeira Copy',
    description: 'Use a IA para criar textos persuasivos de alta conversão.',
    icon: Sparkles,
    view: 'smart_copywriter'
  },
  {
    id: 'page',
    title: 'Criar Landing Page',
    description: 'Coloque sua oferta no ar em menos de 5 minutos.',
    icon: Layers,
    view: 'landing_pages'
  },
  {
    id: 'optimize',
    title: 'Validar Performance',
    description: 'Use o Otimizador Inteligente para prever sua taxa de conversão.',
    icon: Zap,
    view: 'optimizer'
  }
];

export const LaunchChecklist: React.FC = () => {
  const [completedItems, setCompletedItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth.currentUser) return;

    const unsub = onSnapshot(doc(db, 'users', auth.currentUser.uid), (doc) => {
      if (doc.exists()) {
        setCompletedItems(doc.data().launchChecklist || []);
      }
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const toggleItem = async (id: string) => {
    if (!auth.currentUser) return;

    const newItems = completedItems.includes(id)
      ? completedItems.filter(i => i !== id)
      : [...completedItems, id];

    await setDoc(doc(db, 'users', auth.currentUser.uid), {
      launchChecklist: newItems
    }, { merge: true });
  };

  const resetChecklist = async () => {
    if (!auth.currentUser) return;
    await setDoc(doc(db, 'users', auth.currentUser.uid), {
      launchChecklist: []
    }, { merge: true });
  };

  const isSimulationMode = typeof window !== 'undefined' && localStorage.getItem('OPTIVELO_SIMULATION_MODE') === 'true';

  const progress = Math.round((completedItems.length / CHECKLIST_ITEMS.length) * 100);

  if (loading) return null;

  return (
    <div className="premium-card p-8 space-y-8 relative overflow-hidden group">
      {/* Background Glow */}
      <div className="absolute -top-24 -right-24 w-64 h-64 bg-indigo-500/10 rounded-full blur-[80px] group-hover:bg-indigo-500/20 transition-all duration-700" />
      
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500/20 rounded-lg">
              <Rocket className="w-5 h-5 text-indigo-400" />
            </div>
            <h3 className="text-xl font-black text-white tracking-tight uppercase italic">Checklist de Lançamento</h3>
          </div>
          <p className="text-sm text-slate-500 font-medium">Complete os passos essenciais para colocar sua operação no ar.</p>
        </div>

        <div className="flex items-center gap-3">
          {isSimulationMode && (
            <button 
              onClick={resetChecklist}
              className="px-4 py-2 border border-white/5 bg-white/[0.03] text-[12px] font-black text-slate-500 uppercase tracking-widest rounded-xl hover:bg-white/5 transition-all"
            >
              Resetar Progresso
            </button>
          )}
          <div className="flex items-center gap-4 bg-white/[0.03] border border-white/5 p-4 rounded-2xl">
            <div className="text-right">
              <p className="text-[12px] font-black text-slate-500 uppercase tracking-widest">Progresso Total</p>
              <p className="text-2xl font-black text-white">{progress}%</p>
            </div>
            <div className="w-12 h-12 rounded-full border-4 border-white/5 flex items-center justify-center relative">
              <svg className="w-full h-full -rotate-90">
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  fill="transparent"
                  stroke="currentColor"
                  strokeWidth="4"
                  className="text-indigo-500"
                  strokeDasharray={125.6}
                  strokeDashoffset={125.6 - (125.6 * progress) / 100}
                  style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
                />
              </svg>
              <ShieldCheck className={`w-5 h-5 absolute ${progress === 100 ? 'text-emerald-400' : 'text-slate-700'}`} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10">
        {CHECKLIST_ITEMS.map((item) => {
          const isCompleted = completedItems.includes(item.id);
          const Icon = item.icon;

          return (
            <div 
              key={item.id}
              className={`p-5 rounded-2xl border transition-all duration-300 flex items-start gap-4 cursor-pointer group/item ${
                isCompleted 
                  ? 'bg-emerald-500/5 border-emerald-500/20' 
                  : 'bg-white/[0.02] border-white/5 hover:bg-white/[0.05] hover:border-white/10'
              }`}
              onClick={() => toggleItem(item.id)}
            >
              <div className={`mt-1 transition-colors ${isCompleted ? 'text-emerald-400' : 'text-slate-600 group-hover/item:text-slate-400'}`}>
                {isCompleted ? <CheckCircle2 className="w-6 h-6" /> : <Circle className="w-6 h-6" />}
              </div>
              
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between">
                  <h4 className={`font-bold text-sm uppercase tracking-wider transition-colors ${isCompleted ? 'text-emerald-400' : 'text-white'}`}>
                    {item.title}
                  </h4>
                  <Icon className={`w-4 h-4 ${isCompleted ? 'text-emerald-500/40' : 'text-slate-700'}`} />
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">{item.description}</p>
                
                {!isCompleted && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      window.dispatchEvent(new CustomEvent('change-view', { detail: item.view }));
                    }}
                    className="mt-3 flex items-center gap-2 text-[12px] font-black text-indigo-400 uppercase tracking-widest hover:text-indigo-300 transition-colors"
                  >
                    Ir agora <ArrowRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {progress === 100 && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 p-6 rounded-2xl text-center space-y-3 animate-slide-up">
          <div className="flex justify-center">
            <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center text-white shadow-2xl shadow-emerald-500/40">
              <Rocket className="w-6 h-6" />
            </div>
          </div>
          <h4 className="text-lg font-black text-white uppercase tracking-tight">Pronto para o Lançamento!</h4>
          <p className="text-sm text-emerald-300/60 max-w-md mx-auto">Sua estrutura está validada e pronta para escala. Inicie seu tráfego e monitore os resultados no Dashboard.</p>
        </div>
      )}
    </div>
  );
};
